import React, { useReducer, useState } from 'react';
import clsx from 'clsx';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import Switch from '@material-ui/core/Switch';
import GitInfo from 'react-git-info/macro';
import { useSnackbar } from 'notistack';
import { useCookies } from 'react-cookie';
import { useProgressDialog } from '../context/ProgressDialogProvider';
import { BrowserRouter as Router, Switch as RouterSwitch, useLocation } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import TMAppBar from '../lib/AppBar/TMAppBar';
import { ROUTES } from './routes';
import useAuth from '../hooks/useAuth';
import SetLocalStorageVars from '../components/SetLocalStorageVars';
import { isTokenValid } from '../util/token';
import useDrawer from '../hooks/useDrawer';
import TMDialog from '../lib/Dialog/TMDialog';
import reducer from '../util/reducer';
import useIntl from '../hooks/useIntl';
import TMForm from '../lib/Form/TMForm';
import client from '../feathers';
import axios from "axios";
import CookieBanner from '../components/CookieBanner';
import ItsmeConfirmPage from './ItsmeConfirmPage';
import TwoFactorAuthenticationPage from './TwoFactorAuthenticationPage';
import { TWO_FACTOR_AUTH } from '../util/twoFactorAuth';

const initialState = {
  openEula: true,
  eulaAccepted: false,
  latestEulaId: undefined,
};

const drawerWidth = '30%';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      fontFamily: 'Roboto',
    },
    appBar: {
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      height: '12vh',
    },
    appBarShift: {
      width: `calc(100vw - ${drawerWidth})`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: drawerWidth,
    },
    content: {
      flexGrow: 1,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      marginRight: 0,
      overflow: 'auto',
      height: '88vh',
    },
    contentShift: {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: drawerWidth,
    },
    gitInfo: {
      position: 'absolute',
      bottom: '10px',
      left: '10px',
      fontSize: '10px',
      padding: '4px',
      backgroundColor: theme.palette.secondary.dark,
      color: theme.palette.secondary.light,
    },
  })
);

const Layout = (props) => {
  const classes = useStyles();
  const location = useLocation();
  const { showProgressDialog, hideProgressDialog } = useProgressDialog();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { accessToken, user, authenticationFailed } = useAuth();
  const { showDrawer, toggleDrawer } = useDrawer();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { translate } = useIntl();
  const gitInfo = GitInfo();
  const [cookies] = useCookies(['cookieAccepted']);
  // loggedInUser is only used to pass on the user returned by find-user middleware
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isTwoFactorEmailVerified, setIsTwoFactorEmailVerified] = useState(false);

  const showCookieBanner = cookies.cookieAccepted === undefined;

  const helpClicked = () => toggleDrawer();

  const handleChange = (e) => {
    dispatch({
      payload: {
        [e.target.name]: e.target.checked,
        latestEulaId: user.eulaToSign.id,
      },
    });
  };

  const handleAddSavedEula = async () => {
    try {
      showProgressDialog();
      await client.service('users').patch(user.id, { eulaId: state.latestEulaId });
      props.resetAuthentication();
      dispatch({ payload: { openEula: false } });

    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      hideProgressDialog();
    }
  };

  const logout = async () => {
    window.addToDataLayer('eula-not-accepted', 'logout');
    localStorage.clear();
    sessionStorage.clear();
    // call logout
    try {
      let { token } = window.walletApi._getWalletInfo();
      await axios.post(process.env.REACT_APP_OAUTH_REVOKE_ENDPOINT, {token, client_id: process.env.REACT_APP_OAUTH_CLIENT_ID})

    } catch (error) {
      console.error(error);
    } finally {
       dispatch({ payload: { openEula: false, loggedOut: true }});
    }
     window.location.reload();
  }

  const login = () => {
    console.log('Go resetAuthentication')
    props.resetAuthentication();
  }

  const getGitInfo = () => {
    return ["production", "uat", "cmacgm", "hpl"].includes(process.env.REACT_APP_NODE_ENV)
      ? ( <React.Fragment /> ) 
      : ( <div className={classes.gitInfo}>{gitInfo.branch} ({gitInfo.commit.shortHash}: {gitInfo.commit.message})</div>)
  };

  // SCRDEV-3760: auto-login is re-enabled, so this warning is no longer required
  // window.addEventListener('beforeunload', function (e) {
  //   e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
  //   e.returnValue = '';
  // });

  // console.log(">>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>")
  // console.log("user", user, accessToken)
  // console.log("authenticationFailed", authenticationFailed)
  // console.log("isTokenValid()", isTokenValid())
  // console.log("loggedInUser", loggedInUser)
  // console.log("isTwoFactorEmailVerified", isTwoFactorEmailVerified)
  // console.log("<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<<")

  // No login required for onlyt this path
  if (window.location.pathname === "/itsme-confirm") {
    return <ItsmeConfirmPage/>
  }

  // Every other path needs authentication, so let's check that first
  // Is the token no longer valid, or did we lose the loggedInUser due to a refresh? Let's log in again.
  if (!isTokenValid() || loggedInUser === null || authenticationFailed) {
    // explicitly logout in case we are redirect here through wallet token expiration shenanigans
    return <SetLocalStorageVars
              setLoggedInUser={setLoggedInUser} 
              loggedInUser={loggedInUser}
              authenticationFailed={authenticationFailed}
              // this is for auto-login porposes
              setIsTwoFactorEmailVerified={setIsTwoFactorEmailVerified}
              isTwoFactorEmailVerified={isTwoFactorEmailVerified}
            >;
      {showCookieBanner && <CookieBanner position='right' />}
      {getGitInfo()}
      </SetLocalStorageVars>;
  }

  // In case we have a valid token in storage, next up would be the 2FA validation
  if (isTokenValid() && loggedInUser !== null) {
    if (loggedInUser.twoFactorAuth === TWO_FACTOR_AUTH.emailEnabled && !isTwoFactorEmailVerified) {
      // 2FA needed and not done -> redirect to 2FA page
      return <TwoFactorAuthenticationPage 
                loggedInUser={loggedInUser}
                setLoggedInUser={setLoggedInUser}
                isTwoFactorEmailVerified={isTwoFactorEmailVerified}
                setIsTwoFactorEmailVerified={setIsTwoFactorEmailVerified}
              />
    } else {
      if (!authenticationFailed && !user) {
        // No 2FA or 2FA done -> do wallet authentication
        // show loader
        console.log("Do login & show loader")
        login();
        return (
          <div style={{ display: 'flex', height: '60vh', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress size={100} thickness={5} />
          </div>
        )
      } else {
        // Did authentication fail? Back to login it is then.
        if (authenticationFailed) {
          window.location.reload();
        }
        // Did authentication succeed? Good, the next 'if' statements will catch you
      }
    }
  }

  // In case the 2FA validation succeeded (or was not required), we now have a user, and we can check if the user needs to sign a new EULA
  if ( (loggedInUser.twoFactorAuth === TWO_FACTOR_AUTH.disabled || isTwoFactorEmailVerified) && user && accessToken) 
  {
    if (user.hasOwnProperty('eulaToSign')) {
      return (
        <TMDialog
          key="sign-eula"
          title={translate('login.accept.eula')}
          dialogOpen={state.openEula}
          handleDialogClose={logout}
          maxWidth="sm"
          showBottomClose={false}
        >
          <p>
            {translate('eula.intro.1')}
            <br />
            {translate('eula.intro.2')}
          </p>
          <TMForm
            object={state}
            handleChange={handleChange}
            handleSubmit={handleAddSavedEula}
            submitLabel={translate('login.accept.eula.submit')}
            handleCancel={logout}
            cancelLabel={translate('general.cancel')}
            enableSubmitCondition={state.eulaAccepted}
            fields={[
              {
                field: 'eulaAccepted',
                label: translate('order.premium.label.eula'),
                componentType: Switch,
                fullWidth: true,
                required: true,
                placeholder: translate('order.premium.label.eula.accept'),
                helperText: (
                  <a href={user.eulaToSign.url} target="_new">
                    {translate('order.premium.label.eula.read')}
                  </a>
                ),
              },
            ]}
          />
          {getGitInfo()}
          {showCookieBanner && <CookieBanner />}
        </TMDialog>
      );
    } else {
      // All set, let's go !
      return (
        <div className={classes.container}>
          <Router>
            <TMAppBar
              user={user}
              isAuthenticated={!!accessToken}
              handleHelpClick={helpClicked}
              className={clsx(classes.appBar, showDrawer && classes.appBarShift)}
              logoPath="/tmining-logo-mix.png"
            />
            <div
              className={clsx(classes.content, showDrawer && classes.contentShift)}
            >
              {showCookieBanner && <CookieBanner />}
              <RouterSwitch>
                {ROUTES.map(({ path, component, isProtected }) => (
                  <ProtectedRoute
                    key={path}
                    path={`/${path}`}
                    component={component}
                    isProtected={isProtected}
                    exact
                  />
                ))}
              </RouterSwitch>
              {getGitInfo()}
            </div>
          </Router>
        </div>
      );
    }
  }
};

export default Layout;
